import {
  AgGridEvent,
  ColDef,
  ColumnPivotModeChangedEvent,
  GetDataPath,
  GetRowIdFunc,
  GridReadyEvent,
  ICellRendererParams,
  IsFullWidthRowParams,
  RowClassRules,
  RowGroupOpenedEvent,
  RowHeightParams,
} from "@ag-grid-community/core";
import { AgGridReactProps } from "@ag-grid-community/react";
import { GenericTableRow } from "gen/model/genericTableRow";
import { useCallback, useMemo } from "react";
import { useTableBoxContext } from "shared/components/table/box/TableBoxContext";
import { DataTableContext } from "shared/components/table/data/DataTableContext";
import { DataTableProps } from "shared/components/table/data/DataTableProps";
import {
  GenericTableAgGridReactProps,
  isPaginationRow,
  isTotalRow,
} from "shared/components/table/data/DataTableTypes";
import { useDataTableAutoSizeColumnsAgProps } from "shared/components/table/data/internal/ag-props/DataTableAgPropsAutoSizeColumns";
import { useDataTableColumnDefsAgProps } from "shared/components/table/data/internal/ag-props/DataTableAgPropsColumnDefs";
import { useDataTableColumnAgProps } from "shared/components/table/data/internal/ag-props/DataTableAgPropsColumns";
import { dataTableAgPropsExport } from "shared/components/table/data/internal/ag-props/DataTableAgPropsExport";
import { useDataTableOverlayAgProps } from "shared/components/table/data/internal/ag-props/DataTableAgPropsOverlay";
import { useDataTablePaginationAgProps } from "shared/components/table/data/internal/ag-props/DataTableAgPropsPagination";
import { useDataTableRowClickAgProps } from "shared/components/table/data/internal/ag-props/DataTableAgPropsRowClick";
import { useDataTableRowsAgProps } from "shared/components/table/data/internal/ag-props/DataTableAgPropsRows";
import { useDataTableRowSelectionAgProps } from "shared/components/table/data/internal/ag-props/DataTableAgPropsRowSelection";
import { useDataTableStatusBarProps } from "shared/components/table/data/internal/ag-props/DataTableAgPropsStatusBar";
import { onCellFocused } from "shared/components/table/data/internal/DataTableCellFocus";
import { dataTableCellSumAggFunc } from "shared/components/table/data/internal/DataTableCellSumAggFunc";
import { DataTableHiddenRows } from "shared/components/table/data/internal/DataTableHiddenRows";
import { MaybernTableSizes } from "shared/components/table/maybern/MaybernTableConstants";
import { MaybernTableExcelStyle } from "shared/components/table/maybern/MaybernTableExcelStyle";
import { useMaybernTableUIContext } from "shared/components/table/maybern/MaybernTableUIContext";
import { useDetectRenderLoop, useMergedProps } from "shared/utils/ReactUtils";

export const useDataTableAgReactProps = (
  props: DataTableProps,
  tableContext: DataTableContext,
): GenericTableAgGridReactProps => {
  useDetectRenderLoop();

  const {
    data: { client_row_aggregation },
    initialState,
    // column virtualization is disabled by default to make autoSizeAllColumns() work
    //  see: https://www.ag-grid.com/javascript-data-grid/column-sizing/#auto-size-columns
    //  can revisit this if it causes perf issues
    suppressColumnVirtualisation = true,
  } = props;
  const { rowDataPaths, getRowSpanKeyForCell } = tableContext;

  const { setIsReady } = useMaybernTableUIContext();
  const { isFullHeight, isFullScreen, tableBoxRef } = useTableBoxContext();

  const domLayout = isFullHeight || isFullScreen ? undefined : "autoHeight";

  const onGridReadyProps = props.onGridReady;

  const onGridReady: AgGridReactProps["onGridReady"] = useCallback(
    (e: GridReadyEvent) => {
      setIsReady(true);
      onGridReadyProps?.(e);
    },
    [setIsReady, onGridReadyProps],
  );

  const rowClassRules = useMemo<RowClassRules<GenericTableRow>>(
    () => ({
      "mb-row-is-total": (props) =>
        isTotalRow(props.data) || props.data?.granularity_level === "total",
      "mb-row-is-last-pinned-bottom": (props) =>
        props.node.rowPinned === "bottom" &&
        props.node.rowIndex === props.api.getPinnedBottomRowCount() - 1,
      "mb-row-is-clickable": (props) =>
        !!(
          props.node.data &&
          (props.context as DataTableContext).getLinkInfoForRow?.({
            row: props.node,
          })
        ),
      ...props.rowClassRules,
    }),
    [props.rowClassRules],
  );

  const onRowGroupOpened = useCallback(
    (event: RowGroupOpenedEvent<GenericTableRow>) => {
      // Needed to update visibility of "Varies" cells (see DataTableCellRenderer)
      event.api.refreshCells({ rowNodes: [event.node], force: true });
    },
    [],
  );

  const getDataPath = useCallback<GetDataPath<GenericTableRow>>(
    (data) => rowDataPaths.get(data.id) ?? [],
    [rowDataPaths],
  );

  const { enableRangeSelection, suppressCellFocus, onFirstDataRendered } =
    props;

  return useMergedProps<GenericTableAgGridReactProps>(
    {
      rowDragManaged: props.rowDragManaged,
      // Range selection does not work with row spanning
      // https://www.ag-grid.com/javascript-data-grid/row-spanning/#constraints-with-row-spanning
      enableRangeSelection:
        !getRowSpanKeyForCell && enableRangeSelection !== false,
      suppressColumnVirtualisation,
      suppressCellFocus,
      animateRows: props.rowDragManaged ? true : false,
      alwaysAggregateAtRootLevel: true,
      context: tableContext,
      domLayout,
      excelStyles: MaybernTableExcelStyle,
      fullWidthCellRenderer: FullWidthCellRenderer,
      getDataPath: client_row_aggregation ? undefined : getDataPath,
      getRowHeight,
      getRowId,
      groupDisplayType: !client_row_aggregation ? "custom" : undefined,
      groupHeaderHeight: MaybernTableSizes.groupHeaderHeight,
      headerHeight: MaybernTableSizes.headerHeight,
      isFullWidthRow,
      onCellFocused,
      onRowGroupOpened,
      onGridReady,
      onColumnMoved: onColumnMovedOrVisible,
      onColumnVisible: onColumnMovedOrVisible,
      onColumnGroupOpened: onColumnMovedOrVisible,
      onColumnPivotChanged: onColumnMovedOrVisible,
      onFilterChanged: useFilterChanged(props.onFilterChanged),
      onFirstDataRendered,
      onColumnPivotModeChanged,
      // Popups need to be inside the <FocusLock> when full screen. Otherwise,
      // they need to be in the body because the <TableBox> may be very short.
      popupParent: isFullScreen ? tableBoxRef.current : document.body,
      rowClassRules,
      suppressAggFuncInHeader: true,
      suppressPaginationPanel: true,
      suppressRowTransform: true,
      suppressContextMenu: true,
      treeData: !client_row_aggregation,
      reactiveCustomComponents: true,
      processPivotResultColDef,
      aggFuncs: {
        dataTableCellSumAggFunc,
      },
      localeText: {
        dataTableCellSumAggFunc: "sum",
      },
      sideBar: props.sideBar || {
        toolPanels: ["columns", "filters"],
        hiddenByDefault: true,
      },
      initialState,
      onStateUpdated: props.onStateUpdated,
      onRowDragEnter: props.onRowDragEnter,
      onRowDragEnd: props.onRowDragEnd,
      onRowDragLeave: props.onRowDragLeave,
      rowDragText: props.rowDragText,
    },
    useDataTableOverlayAgProps(props, tableContext),
    useDataTableColumnDefsAgProps(props),
    useDataTableAutoSizeColumnsAgProps(props),
    useDataTableRowSelectionAgProps(props),
    useDataTableRowClickAgProps(tableContext),
    useDataTableColumnAgProps(props, tableContext),
    useDataTablePaginationAgProps(props),
    useDataTableRowsAgProps(props, tableContext),
    useDataTableStatusBarProps(props),
    dataTableAgPropsExport,
  );
};

const getRowId: GetRowIdFunc<GenericTableRow> = (row) => row.data.id;

const FullWidthCellRenderer: React.FC<ICellRendererParams<GenericTableRow>> = ({
  node,
}) => (isPaginationRow(node.data) ? <DataTableHiddenRows /> : undefined);

const getRowHeight = ({ node }: RowHeightParams) =>
  isPaginationRow(node.data)
    ? MaybernTableSizes.paginationRowHeight
    : undefined;

const isFullWidthRow = ({ rowNode }: IsFullWidthRowParams) =>
  isPaginationRow(rowNode.data);

const onColumnMovedOrVisible = (event: AgGridEvent) => {
  // This is needed to update position-related css classes
  // NOTES:
  // 1. If this is called too early (e.g. in onDisplayedColumnsChanged),
  //    it will interrupt the initial autoSizeAllColumns().
  // 2. The setTimeout is here to give the hide/show animation time to complete,
  //    otherwise the columns get messed up due to AG Grid bug:
  //    https://ag-grid.zendesk.com/hc/en-us/requests/31765
  setTimeout(() => {
    if (!event.api.isDestroyed()) {
      event.api.refreshHeader();
      event.api.refreshCells({ force: true });
    }
  }, 500);
};

const useFilterChanged = (
  filterChangedCb: AgGridReactProps["onFilterChanged"],
) => {
  const onFilterChanged = useCallback<
    NonNullable<AgGridReactProps["onFilterChanged"]>
  >(
    (event) => {
      filterChangedCb?.(event);
      // refresh row groups so "Varies" cells are updated to reflect children after filtering
      event.api.refreshCells({ force: true });
    },
    [filterChangedCb],
  );
  return onFilterChanged;
};

const onColumnPivotModeChanged = (event: ColumnPivotModeChangedEvent) => {
  if (!event.api.isPivotMode()) {
    // reset grid state
    event.api.setPivotResultColumns(null);
    event.api.setRowGroupColumns([]);
    setTimeout(() => {
      // for now don't allow using the tool panel when not pivoting
      // timeout makes the UI less jarring
      event.api.setSideBarVisible(false);
    }, 500);
  }
};

const processPivotResultColDef = (colDef: ColDef<GenericTableRow>) => {
  colDef.valueGetter = (params) => params.data?.cells[params.colDef.colId!];
};
